
import Vue from 'vue';
import api from '@/rest-api';
import { groupBy, notEmpty, uniqueIndex } from '@/util/array-util';
import { registerSelectionHandler, NodeSelection, SelectionHandler } from '@/util/text-selection';
import {
  FileDescriptor,
  IngestBox, IngestDocument, IngestFragment, IngestPage, Dim, Pos, UUID, IngestTextFragment, IngestFragmentBox, FileDocument, Suggestion, isIngestTextFragment,
} from '@/model';
import DeferredMount from '@/components/DeferredMount.vue';
import Progress from '@/components/Progress.vue';
import { PDFController, PDFControllerImpl } from '@/components/pdf/PDFContext';
import PDFLoadingPage from '@/components/pdf/PDFLoadingPage.vue';
import DocumentTextBox from '@/components/DocumentTextBox.vue';
import DocumentTableBox from '@/components/DocumentTableBox.vue';
import FragmentSelectionToolbox from '@/components/FragmentSelectionToolbox.vue';
import deepEqual from '@/util/deep-equals';

export default Vue.extend({
  name: 'IngestDocumentViewer',
  components: {
    DeferredMount,
    Progress,
    PDFLoadingPage,
    DocumentTextBox,
    DocumentTableBox,
    FragmentSelectionToolbox,
  },

  props: {
    document: { type: Object as () => FileDocument, required: true },
    ingest: { type: Object as () => IngestDocument | undefined, required: false },
    media: { type: Array as () => FileDescriptor[], default: () => [] },
    showIngest: { type: Boolean, default: false },
    fragments: { type: Array as () => IngestFragment[], default: () => [] },
    fontScale: { type: Number, default: 1.0 },
    styleOn: { type: Boolean, default: true },
    colors: { type: Boolean, default: true },
    edit: { type: Object as () => IngestFragment },
    zoom: { type: Number, default: 0.75 },
    highlights: { type: Array as () => IngestFragmentBox[] | undefined, required: false },
    wrapText: { type: Boolean, default: false },
    suggestions: { type: Array as () => Suggestion[] | undefined },
    activeHierarchyId: { type: Number as () => number | null, required: false },
    scrollToHierarchyId: { type: Number as () => number | null, required: false },
    hideImageHierarchyContainers: { type: Boolean, default: false },
  },

  data() {
    return {
      pdf: undefined as undefined | PDFController,
      selectionHandler: undefined as SelectionHandler | undefined,
      selection: undefined as NodeSelection | undefined,
      fragmentsByBoxId: {} as Record<number, IngestFragment[]>,
      suggestionsByBoxId: {} as Record<number, Suggestion[]>,
      clickedBox: undefined as IngestBox | undefined,
      viewerScrollWidth: 0,
    };
  },

  mounted() {
    this.viewerScrollWidth = (this.$el as HTMLElement).clientWidth;
    if (this.document.file) {
      this.pdf = new PDFControllerImpl(this.fileSource(this.document.file.uuid), 10);
    }
    this.selectionHandler = registerSelectionHandler(
      document,
      (selection) => { this.selection = selection; },
      (node) => node === this.$refs.viewer,
      (node) => !!node.attributes.getNamedItem('textIndex'),
      'textIndex', 'boxIndex', 'pageIndex',
    );
  },

  beforeDestroy() {
    if (this.selectionHandler) {
      this.selectionHandler.deregister();
    }
  },

  watch: {
    fragments: {
      immediate: true,
      handler(to: IngestTextFragment[] | undefined) {
        if (this.selection && this.selectionHandler) {
          this.selectionHandler.deselect();
        }
        const y = groupBy((to || []).flatMap((f) => f.boxes.map((b) => ({ ...f, boxId: b.boxId }))), (b) => b.boxId);
        for (const [k, e] of Object.entries(this.fragmentsByBoxId)) {
          const key = parseInt(k, 10);
          if (!deepEqual(e, y.get(key))) {
            Vue.set(this.fragmentsByBoxId, k, y.get(key));
          }
        }
        for (const k of y.keys()) {
          if (!(k in this.fragmentsByBoxId)) {
            Vue.set(this.fragmentsByBoxId, k, y.get(k));
          }
        }
        this.$nextTick(() => this.emitTableBoxFragments(to || []));
      },
    },
    suggestions: {
      immediate: true,
      handler(to: Suggestion[] | undefined) {
        const y = groupBy((to || []).filter((s) => s.confidence !== undefined && s.confidence > 0.5), (s) => s.boxId);
        for (const [k, e] of Object.entries(this.suggestionsByBoxId)) {
          const key = parseInt(k, 10);
          if (!deepEqual(e, y.get(key))) {
            Vue.set(this.suggestionsByBoxId, k, y.get(key));
          }
        }
        for (const k of y.keys()) {
          if (!(k in this.suggestionsByBoxId)) {
            Vue.set(this.suggestionsByBoxId, k, y.get(k));
          }
        }
      },
    },
    ingest() {
      this.$nextTick(() => {
        this.viewerScrollWidth = (this.$el as HTMLElement).clientWidth;
      });
    },
    scrollToHierarchyId: {
      immediate: false,
      handler(id: number | null | undefined) {
        this.scrollToHierarchy(id);
      },
    },
  },

  computed: {
    numberOfPages(): Promise<number> {
      // eslint-disable-next-line vue/no-async-in-computed-properties
      return this.pdf ? this.pdf.numberOfPages() : Promise.resolve(this.ingest?.pages.length || 0);
    },
    showingIngest(): boolean {
      return this.showIngest || !this.document.file;
    },
    fileDataLookup(): ReadonlyMap<UUID, FileDescriptor> {
      return uniqueIndex(this.media, 'uuid');
    },
    viewerWidth(): number {
      return this.viewerScrollWidth - 100;
    },
    pageWidth(): number {
      return this.viewerWidth * this.zoom;
    },
    annotatedPages(): number[] {
      const boxes = this.fragments
        .filter((f): f is IngestTextFragment => f.type === 'text')
        .flatMap((f) => f.boxes.map((b) => b.boxId));
      return this.ingest?.pages
        .filter((p) => p.boxes.filter((b) => boxes.findIndex((a) => a === b.id) > -1).length > 0)
        .map((p) => p.seqNo + 1) || [];
    },
    hierarchyIndex(): Map<number, { boxIds: number[]; type?: string }> {
      const map = new Map<number, { boxIds: number[]; type?: string }>();
      if (this.ingest?.hierarchies && this.ingest.hierarchies.length > 0) {
        this.ingest.hierarchies.forEach((h) => {
          map.set(h.id, { boxIds: [...h.boxIds], type: h.type || undefined });
        });
        return map;
      }
      // Fallback if hierarchies are missing but boxes still carry hierarchy ids.
      (this.ingest?.pages || []).forEach((page) => {
        page.boxes.forEach((box) => {
          if (!box.hierarchyId) {
            return;
          }
          const existing = map.get(box.hierarchyId);
          if (existing) {
            existing.boxIds.push(box.id);
          } else {
            map.set(box.hierarchyId, { boxIds: [box.id] });
          }
        });
      });
      return map;
    },
    hierarchyMeta(): Map<number, { type?: string; hasChildren: boolean }> {
      const map = new Map<number, { type?: string; hasChildren: boolean }>();
      const childrenByParent = new Map<number, number[]>();
      (this.ingest?.hierarchies || []).forEach((h) => {
        if (h.parentId != null) {
          const children = childrenByParent.get(h.parentId) || [];
          children.push(h.id);
          childrenByParent.set(h.parentId, children);
        }
      });
      (this.ingest?.hierarchies || []).forEach((h) => {
        map.set(h.id, {
          type: h.type || undefined,
          hasChildren: (childrenByParent.get(h.id) || []).length > 0,
        });
      });
      return map;
    },
  },

  methods: {
    filterBoxes(boxes: readonly IngestBox[]): IngestBox[] {
      let filtered = [...boxes];
      if (this.hideImageHierarchyContainers) {
        filtered = filtered.filter((box) => !this.isImageHierarchyContainer(box));
      }
      return filtered;
    },

    ingestPage(pageNo: number) {
      return this.ingest?.pages[pageNo - 1];
    },
    pageStyle(page: IngestPage | undefined, width: number) {
      const dim = this.dimStyle({ width });
      if (page?.width && page?.height) {
        return dim;
      } else {
        // free flow
        return {
          ...dim,
          padding: '64px',
        };
      }
    },
    pageSpacerStyle(page: IngestPage | undefined) {
      if (!page) return {};
      return this.dimStyle(this.scale(page, page.width));
    },
    dimStyle(dim: Partial<Dim>) {
      const style: any = {};
      if (dim.width) {
        style.width = `${dim.width}px`;
      }
      if (dim.height) {
        style.height = `${dim.height}px`;
      }
      return style;
    },
    boxStyle(box: IngestBox, page: IngestPage | undefined) {
      if (page?.width && page?.height) {
        const rotation = (box.rotation || 0) % 360;
        const scaled = this.scale(box, page.width);
        const transposed = this.transpose(box, scaled.scale);
        // Font size in document units (scale applied once at the end).
        // Use explicit font size from document if available, otherwise fall back to a
        // fixed 12px screen size (converted to document units) to avoid multi-line
        // boxes producing oversized text from a height-based heuristic.
        const defaultFontSize = box.fontSize || (12 / scaled.scale);
        const fontSize = defaultFontSize * scaled.scale * this.fontScale;
        const wordMargin = (box.wordMargin || 0.1) * 100;
        // Default line height in document units
        const defaultLineHeight = box.lineHeight || (defaultFontSize * 1.2);
        const lineHeight = defaultLineHeight * scaled.scale;
        const writingMode = (rotation === 270) ? 'vertical-rl' as const : 'horizontal-tb' as const;
        const transform = (rotation === 270) ? 'rotate(180deg)' : 'rotate(0deg)';
        // skip whiteSpace: 'pre' to allow wrapping
        const whiteSpace = this.wrapText ? {} : { whiteSpace: 'pre' as const };
        const dims = {
          position: 'absolute' as const,
          left: `${transposed.x}px`,
          top: `${transposed.y}px`,
          width: `${scaled.width}px`,
          height: `${scaled.height}px`,
          ...whiteSpace,
        };
        if (this.styleOn) {
          return {
            ...dims,
            fontSize: `${fontSize}px`,
            lineHeight: `${lineHeight}px`,
            wordSpacing: `${wordMargin}%`,
            writingMode,
            transform,
          } as const;
        }
        return dims;
      } else {
        // free flow - use 14px as a comfortable default reading size
        const defaultFreeFlowSize = 14;
        return {
          whiteSpace: 'pre-wrap' as const,
          marginBottom: '20px',
          fontSize: this.styleOn ? `${(box.fontSize || defaultFreeFlowSize) * this.fontScale}px` : undefined,
          lineHeight: this.styleOn ? '1.4' : undefined,
        };
      }
    },
    scale(dim: Dim, pageWidth: number): Dim & { scale: number } {
      const max = this.pageWidth;
      const scale = max / pageWidth;
      return { scale, width: dim.width * scale, height: dim.height * scale };
    },
    transpose(pos: Pos, scale: number): Pos {
      return { x: pos.x * scale, y: pos.y * scale };
    },
    isTableBox(box: IngestBox): boolean {
      return box.type === 'table';
    },
    onBoxClick(box: IngestBox, event: MouseEvent) {
      if (event.detail === 3 && box.text) {
        this.selectWholeBoxText(box);
        return;
      }
      if (this.isTableBox(box) || this.isImageBox(box)) {
        this.clickedBox = this.clickedBox?.id === box.id ? undefined : box;
      }
    },
    selectWholeBoxText(box: IngestBox) {
      const el = document.getElementById(`box-${box.id}`);
      if (!el) {
        return;
      }
      // Deferred so it overrides the browser's native triple-click paragraph selection,
      // which is applied after this event handler runs.
      setTimeout(() => {
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        let firstText: Text | null = null;
        let lastText: Text | null = null;
        for (let node = walker.nextNode(); node; node = walker.nextNode()) {
          firstText = firstText || (node as Text);
          lastText = node as Text;
        }
        if (!firstText || !lastText) {
          return;
        }
        // Anchor on the text nodes (rather than the box element) so the selection
        // handler's start/endContainer parsing, which expects text nodes, works.
        const range = document.createRange();
        range.setStart(firstText, 0);
        range.setEnd(lastText, lastText.length);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }, 0);
    },
    wholeBoxFragment(box: IngestBox): IngestTextFragment {
      return {
        id: 0,
        type: 'text',
        boxes: [{
          boxId: box.id, from: 0, to: box.text?.length || 0, seqNo: 0,
        }],
        labels: [],
        isolate: false,
      };
    },
    addBoxFragment(box: IngestBox) {
      const existing = this.edit && isIngestTextFragment(this.edit) ? this.edit : undefined;
      if (existing) {
        if (!existing.boxes.some((b) => b.boxId === box.id)) {
          this.$emit('selection', {
            ...existing,
            boxes: [...existing.boxes, {
              boxId: box.id, from: 0, to: box.text?.length || 0, seqNo: existing.boxes.length,
            }],
          });
        }
      } else {
        this.$emit('selection', this.wholeBoxFragment(box));
        this.emitBoxRendered(box);
      }
      this.clickedBox = undefined;
    },
    emitTableBoxFragments(fragments: IngestTextFragment[]) {
      const allBoxes = (this.ingest?.pages || []).flatMap((p) => p.boxes);
      const tableBoxIds = new Set(allBoxes.filter((b) => this.isTableBox(b)).map((b) => b.id));
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      const noop = () => {};
      const rendered: Array<{ id: number; top: number; first: boolean; actual: boolean; hover: () => void }> = [];
      fragments.forEach((fragment) => {
        const tableBox = fragment.boxes.find((b) => tableBoxIds.has(b.boxId));
        if (!tableBox) return;
        const el = document.getElementById(`box-${tableBox.boxId}`);
        const rect = el?.getBoundingClientRect();
        if (rect) {
          rendered.push({
            id: fragment.id, top: rect.top + window.pageYOffset, first: true, actual: true, hover: noop,
          });
        }
      });
      if (rendered.length > 0) {
        this.$emit('fragments-rendered', rendered);
      }
    },
    emitBoxRendered(box: IngestBox) {
      const el = document.getElementById(`box-${box.id}`);
      const rect = el?.getBoundingClientRect();
      if (rect) {
        const top = rect.top + window.pageYOffset;
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        const noop = () => {};
        this.$emit('fragments-rendered', [{
          id: 0, top, first: true, actual: true, hover: noop,
        }]);
      }
    },
    removeBoxFragment(box: IngestBox) {
      const existing = this.edit && isIngestTextFragment(this.edit) ? this.edit : undefined;
      if (existing) {
        this.$emit('selection', {
          ...existing,
          boxes: existing.boxes.filter((b) => b.boxId !== box.id),
        });
      }
      this.clickedBox = undefined;
    },
    isImageBox(box: IngestBox): boolean {
      return !!box.file && !!this.fileDataLookup.get(box.file)?.mime.startsWith('image/');
    },
    fileSource(uuid: UUID): string {
      return api.fileDownloadUri(uuid);
    },
    onFragmentsRendered(fragments: Array<{ id: number; top: number; actual: boolean }>) {
      this.$emit('fragments-rendered', fragments);
    },
    onSelection(fragment: IngestFragment) {
      this.$emit('selection', fragment);
    },
    ratio(page: IngestPage | undefined): number | undefined {
      return page ? page.height / page.width : undefined;
    },
    boxSuggestions(boxId: number): Suggestion[] {
      if (!this.suggestions) {
        return [];
      }
      const suggestions = this.suggestions
        .filter((s) => s.boxId === boxId)
        .filter((s) => s.confidence !== undefined && s.confidence > 0.5);
      return suggestions;
    },
    boxHighlights(boxId: number): IngestFragmentBox[] | undefined {
      if (!this.highlights) {
        return undefined;
      }
      const highlights = this.highlights.filter((h) => h.boxId === boxId);
      return highlights.length > 0 ? highlights : undefined;
    },

    boxMaxConfidence(boxId: number): number {
      return Math.max(...this.suggestionsByBoxId[boxId].map((s) => s.confidence || 0));
    },
    falsePositive(suggestions: Suggestion[]): boolean {
      const actions = suggestions.map((s) => s.action).filter(notEmpty);
      if (actions.length === 0) {
        return false;
      }
      // if all suggestions with action with no fragmentId
      return actions.every((a) => a.fragmentId === undefined);
    },
    truePositive(suggestions: Suggestion[], fragments: IngestFragment[]): boolean {
      // if suggestion with action with fragmentId
      return (fragments.length > 0 && suggestions.length > 0) || suggestions.some((s) => s.action?.fragmentId !== undefined);
    },
    boxClass(box: IngestBox): string[] {
      const classes: string[] = [];
      const suggestions = this.suggestionsByBoxId[box.id];
      const fragments = this.fragmentsByBoxId[box.id] || [];
      if (suggestions && suggestions.length > 0) {
        if (this.falsePositive(suggestions)) {
          classes.push('false-positive');
        } else if (this.truePositive(suggestions, fragments)) {
          classes.push('true-positive');
        } else {
          classes.push('relevant');
        }
      }
      if (this.activeHierarchyId && box.hierarchyId === this.activeHierarchyId) {
        classes.push('hierarchy-related');
      }
      return classes;
    },
    hierarchyGroup(box: IngestBox): { id: number; size: number; type?: string } | undefined {
      if (!box.hierarchyId) {
        return undefined;
      }
      const group = this.hierarchyIndex.get(box.hierarchyId);
      if (!group) {
        return undefined;
      }
      return { id: box.hierarchyId, size: group.boxIds.length, type: group.type };
    },
    hierarchyTitle(box: IngestBox): string | undefined {
      const group = this.hierarchyGroup(box);
      if (!group) {
        return undefined;
      }
      const type = group.type ? ` • ${group.type}` : '';
      return `Hierarchy ${group.id} (${group.size} items${type})`;
    },
    isImageHierarchyContainer(box: IngestBox): boolean {
      if (!box.hierarchyId) {
        return false;
      }
      const meta = this.hierarchyMeta.get(box.hierarchyId);
      if (!meta || !meta.hasChildren) {
        return false;
      }
      const type = (meta.type || '').toLowerCase();
      return type.includes('image') || type.includes('figure');
    },
    scrollToHierarchy(hierarchyId: number | null | undefined) {
      if (!hierarchyId) {
        return;
      }
      const group = this.hierarchyIndex.get(hierarchyId);
      const firstBoxId = group?.boxIds?.[0];
      if (firstBoxId) {
        const el = document.getElementById(`box-${firstBoxId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }
      }
    },
  },

});
